import { RouteLocationNormalized } from 'vue-router';
import {
    getEnrichedRoute,
} from '@/router/helpers';
import { SIDEBAR_POSITIONS, BkNextMainModuleRouteInterface } from '@/router/helpers/constants';
import useIdentifyService from '@/composables/useElementIdentifierService';
import { INVENTORY_ROUTES_NAMES, LEGACY_ROUTE_NAME } from '@/constants/routeNames';
import { BOOKINGKIT_ROLES } from '@/constants/roles';
import { useAuthenticationService } from '@/composables/useAuthenticationService';
import {
    MODULE_CROSS_SELLING,
    MODULE_FLEX_TICKET, MODULE_FLEXIBLE_PRICING,
    MODULE_PRODUCT_BUNDLES,
    MODULE_RESOURCE_MGMT,
} from '@/models/Modules';
import { ManticoreComponentProps } from '@/components/Manticore/useManticoreNavigation';
import { INVENTORY_MODULE } from '@/constants/modules';
import { getModuleMessages } from './locales';
import Home from './index.vue';

const Manticore = () => import('@/components/Manticore/index.vue');
const CancellationPoliciesComponent = () => import('./pages/CancellationPolicies/index.vue');
const PoliciesComponent = () => import('./pages/CancellationPolicies/components/Policies.vue');
const AssignToInventoryComponent = () => import('./pages/CancellationPolicies/components/AssignToInventory.vue');
const WithdrawalPageComponent = () => import('./pages/CancellationPolicies/components/WithdrawalPage.vue');
const ExperiencesTabComponent = () => import('./pages/CancellationPolicies/components/AssignToInventory/ExperiencesTab.vue');
const FlexOffersTabComponent = () => import('./pages/CancellationPolicies/components/AssignToInventory/FlexOffersTab.vue');
const VouchersTabComponent = () => import('./pages/CancellationPolicies/components/AssignToInventory/VouchersTab.vue');
const CreateCancellationPolicyComponent = () => import('./pages/CreateCancellationPolicy/index.vue');

const { identify } = useIdentifyService({ prefix: 'navBlock.inventory' });
const routes = {
    path: `/${INVENTORY_ROUTES_NAMES.MAIN}`,
    name: INVENTORY_ROUTES_NAMES.MAIN,
    label: 'sidenav.inventory.main.label',
    hiddenRoutes: [
        LEGACY_ROUTE_NAME.EVENT_RULE,
        LEGACY_ROUTE_NAME.EVENT_DATE_DUPLICATE,
        LEGACY_ROUTE_NAME.EVENT_PRICING,
        INVENTORY_ROUTES_NAMES.EXPERIENCE_DETAIL,
        INVENTORY_ROUTES_NAMES.EXPERIENCE_PRICING,
        INVENTORY_ROUTES_NAMES.OFFERS_DETAIL,
        INVENTORY_ROUTES_NAMES.OFFERSDUPLICATE,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_LIST,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_EXPERIENCES,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_FLEX_OFFERS,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_VOUCHERS,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_WITHDRAWAL,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_CREATE,
        INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_EDIT,
    ],
    component: Home,
    meta: {
        sidenav: {
            parent: true,
            position: SIDEBAR_POSITIONS.TOP,
            icon: 'box',
        },
        title: 'sidenav.inventory.main.label',
        moduleName: INVENTORY_MODULE,
        loadModuleAssets: getModuleMessages,
        manticoreMatches: [/event\/marketingContent+(([?/])+.*)?/],
    },
    children: [
        {
            path: `/${INVENTORY_ROUTES_NAMES.MAIN}/`,
            name: INVENTORY_ROUTES_NAMES.EXPERIENCES,
            label: 'sidenav.inventory.experiences.label',
            component: Manticore,
            props: (route:RouteLocationNormalized):ManticoreComponentProps => {
                const iFrameUrl = `event/index/${route.params.code}`;
                return {
                    iFrameUrl,
                };
            },
            meta: {
                title: 'sidenav.inventory.experiences.label',
                manticoreMatches: [/event\/index+(([?/])+.*)?/, /eventRule\/updatePricing+(([?/])+.*)?/,
                    /eventRule\/delete+(([?/])+.*)?/, /event\/updatePricing+(([?/])+.*)?/,
                ],
            },
            'data-pw': identify('experiences'),
        },
        {
            path: `/${INVENTORY_ROUTES_NAMES.MAIN}/:id`,
            name: INVENTORY_ROUTES_NAMES.EXPERIENCE_DETAIL,
            label: 'sidenav.inventory.experiences.label',
            component: Manticore,
            props: (route:RouteLocationNormalized):ManticoreComponentProps => ({
                iFrameUrl: `event/update/${route.params.id}`,
            }),
            meta: {
                title: 'sidenav.inventory.experiences.label',
                manticoreMatches: [/event\/update\/+(?<id>[^?]+)?/],
            },
            'data-pw': identify('experience-details'),
        },
        {
            path: `/${INVENTORY_ROUTES_NAMES.MAIN}/:id/pricing`,
            name: INVENTORY_ROUTES_NAMES.EXPERIENCE_PRICING,
            label: 'sidenav.inventory.experiences.label',
            component: Manticore,
            props: (route:RouteLocationNormalized):ManticoreComponentProps => ({
                iFrameUrl: `event/pricing/${route.params.id}`,
            }),
            meta: {
                title: 'sidenav.inventory.experiences.label',
                manticoreMatches: [/event\/pricing\/+(?<id>[^?]+)?/],
            },
            'data-pw': identify('experience-pricing'),
        },
        {
            path: INVENTORY_ROUTES_NAMES.OFFERS,
            name: INVENTORY_ROUTES_NAMES.OFFERS,
            label: 'sidenav.inventory.flexibleOffers.label',
            component: Manticore,
            meta: {
                title: 'sidenav.inventory.flexibleOffers.label',
                modulesToBeActive: [MODULE_FLEX_TICKET],
                manticoreMatches: [/flexOffer\/index+(([?/])+.*)?/, /flexOffer\/create+(([?/])+.*)?/],
            },
            props: {
                iFrameUrl: 'flexOffer/index',
            },
            'data-pw': identify('flexibleOffers'),
        },
        {
            path: `${INVENTORY_ROUTES_NAMES.OFFERS}/:id`,
            name: INVENTORY_ROUTES_NAMES.OFFERS_DETAIL,
            label: 'sidenav.inventory.flexibleOffers.label',
            component: Manticore,
            props: (route:RouteLocationNormalized):ManticoreComponentProps => ({
                iFrameUrl: `flexOffer/update/${route.params.id}`,
            }),
            meta: {
                title: 'sidenav.inventory.flexibleOffers.label',
                modulesToBeActive: [MODULE_FLEX_TICKET],
                manticoreMatches: [/flexOffer\/update\/+(?<id>[^?]+)?/],
            },
            'data-pw': identify('flexibleOffersDetail'),
        },
        {
            path: `/${INVENTORY_ROUTES_NAMES.OFFERSDUPLICATE}/:code`,
            name: INVENTORY_ROUTES_NAMES.OFFERSDUPLICATE,
            label: 'sidenav.inventory.flexibleOffersDuplicate.label',
            component: Manticore,
            meta: {
                title: 'sidenav.inventory.flexibleOffersDuplicate.label',
                modulesToBeActive: [MODULE_FLEX_TICKET, MODULE_FLEXIBLE_PRICING],
                manticoreMatches: [/flexOffer\/duplicate\/+(?<code>[^?]+)?/],
            },
            props: (route:RouteLocationNormalized):ManticoreComponentProps => ({
                iFrameUrl: `flexOffer/duplicate/${route.params.id}`,
            }),
            'data-pw': identify('flexibleOffersDuplicate'),
        },
        {
            path: INVENTORY_ROUTES_NAMES.PRODUCTS,
            name: INVENTORY_ROUTES_NAMES.PRODUCTS,
            label: 'sidenav.inventory.products.label',
            component: Manticore,
            meta: {
                title: 'sidenav.inventory.products.label',
                manticoreMatches: [/product\/index+(([?/])+.*)?/],
                modulesToBeActive: [MODULE_CROSS_SELLING],
            },
            props: {
                iFrameUrl: 'product/index',
            },
            'data-pw': identify('products'),
        },
        {
            path: INVENTORY_ROUTES_NAMES.BUNDLES,
            name: INVENTORY_ROUTES_NAMES.BUNDLES,
            label: 'sidenav.inventory.bundles.label',
            component: Manticore,
            meta: {
                title: 'sidenav.inventory.bundles.label',
                manticoreMatches: [/product\/bundles+(([?/])+.*)?/, /product\/bundle+.*/],
                modulesToBeActive: [MODULE_PRODUCT_BUNDLES],
            },
            props: {
                iFrameUrl: 'product/bundles',
            },
            'data-pw': identify('bundles'),
        },
        {
            path: INVENTORY_ROUTES_NAMES.RESOURCES,
            name: INVENTORY_ROUTES_NAMES.RESOURCES,
            label: 'sidenav.inventory.resources.label',
            component: Manticore,
            meta: {
                title: 'sidenav.inventory.resources.label',
                manticoreMatches: [/resource\/index+(([?/])+.*)?/],
                modulesToBeActive: [MODULE_RESOURCE_MGMT],
            },
            props: {
                iFrameUrl: 'resource/index',
            },
            'data-pw': identify('resources'),
        },
        {
            path: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES,
            name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES,
            label: 'sidenav.inventory.cancellationPolicies.label',
            component: CancellationPoliciesComponent,
            meta: {
                title: 'sidenav.inventory.cancellationPolicies.label',
                manticoreMatches: undefined,
            },
            'data-pw': identify('cancellationPolicies'),
            redirect: { name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_LIST },
            children: [
                {
                    path: 'policies',
                    name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_LIST,
                    label: 'sidenav.inventory.cancellationPolicies.label',
                    component: PoliciesComponent,
                    meta: {
                        title: 'sidenav.inventory.cancellationPolicies.label',
                    },
                },
                {
                    path: 'assign-to-inventory',
                    name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN,
                    label: 'sidenav.inventory.cancellationPolicies.label',
                    component: AssignToInventoryComponent,
                    meta: {
                        title: 'sidenav.inventory.cancellationPolicies.label',
                    },
                    redirect: () => {
                        const { hasRole } = useAuthenticationService();
                        return {
                            name: hasRole(BOOKINGKIT_ROLES.admin)
                                ? INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_EXPERIENCES
                                : INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_FLEX_OFFERS,
                        };
                    },
                    children: [
                        {
                            path: 'experiences',
                            name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_EXPERIENCES,
                            label: 'sidenav.inventory.cancellationPolicies.label',
                            component: ExperiencesTabComponent,
                            meta: {
                                title: 'sidenav.inventory.cancellationPolicies.label',
                                roleRequired: BOOKINGKIT_ROLES.admin,
                            },
                        },
                        {
                            path: 'flex-offers',
                            name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_FLEX_OFFERS,
                            label: 'sidenav.inventory.cancellationPolicies.label',
                            component: FlexOffersTabComponent,
                            meta: {
                                title: 'sidenav.inventory.cancellationPolicies.label',
                            },
                        },
                        {
                            path: 'vouchers',
                            name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_ASSIGN_VOUCHERS,
                            label: 'sidenav.inventory.cancellationPolicies.label',
                            component: VouchersTabComponent,
                            meta: {
                                title: 'sidenav.inventory.cancellationPolicies.label',
                            },
                        },
                    ],
                },
                {
                    path: 'withdrawal',
                    name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_WITHDRAWAL,
                    label: 'sidenav.inventory.cancellationPolicies.label',
                    component: WithdrawalPageComponent,
                    meta: {
                        title: 'sidenav.inventory.cancellationPolicies.label',
                    },
                },
            ],
        },
        {
            path: 'cancellation-policies/create',
            name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_CREATE,
            label: 'sidenav.inventory.cancellationPolicies.label',
            component: CreateCancellationPolicyComponent,
            meta: {
                title: 'sidenav.inventory.cancellationPolicies.label',
                manticoreMatches: undefined,
            },
            'data-pw': identify('createCancellationPolicy'),
        },
        {
            path: 'cancellation-policies/edit/:policyId',
            name: INVENTORY_ROUTES_NAMES.CANCELLATION_POLICIES_EDIT,
            label: 'sidenav.inventory.cancellationPolicies.label',
            component: CreateCancellationPolicyComponent,
            meta: {
                title: 'sidenav.inventory.cancellationPolicies.label',
                manticoreMatches: undefined,
            },
            'data-pw': identify('editCancellationPolicy'),
        },
    ],
} as BkNextMainModuleRouteInterface;

export default getEnrichedRoute(routes, INVENTORY_MODULE, getModuleMessages);
